import { TIMING } from '@shared/constants/limits';
import type { ClientMessage, ServerMessage } from '@shared/types/messages';
import type { PublicRoomState, SelectionStage } from '@shared/types/room';
import { create } from 'zustand';
import { clearSession } from '@/services/session';
import { RoomSocket, type SocketStatus } from '@/services/socket';
import { toast } from './toastStore';

/** 선택 결과 공개 애니메이션 상태 */
export interface Reveal {
  stage: SelectionStage;
  optionId: string;
  optionIds: string[];
  isRandom: boolean;
  isAuto: boolean;
  participantNickname: string;
  durationMs: number;
  startedAt: number;
}

export type ClosedReason = 'host' | 'expired' | 'empty' | 'kicked' | 'unauthorized' | 'left';

interface RoomStore {
  roomId: string | null;
  selfId: string | null;
  state: PublicRoomState | null;
  connection: 'idle' | SocketStatus;
  closedReason: ClosedReason | null;
  reveal: Reveal | null;
  socket: RoomSocket | null;
  connect: (roomId: string, participantId: string, token: string) => void;
  disconnect: () => void;
  send: (message: ClientMessage) => boolean;
  leave: () => void;
  finishReveal: () => void;
  reset: () => void;
}

const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useRoomStore = create<RoomStore>((set, get) => ({
  roomId: null,
  selfId: null,
  state: null,
  connection: 'idle',
  closedReason: null,
  reveal: null,
  socket: null,

  connect: (roomId, participantId, token) => {
    get().socket?.close();
    const socket = new RoomSocket(roomId, participantId, token, {
      onStatus: (status) => {
        set({ connection: status });
      },
      onMessage: (message) => handleMessage(message, set, get),
    });
    set({ roomId, selfId: participantId, socket, connection: 'connecting', closedReason: null, reveal: null, state: null });
    socket.connect();
  },

  disconnect: () => {
    get().socket?.close();
    set({ socket: null, connection: 'idle' });
  },

  send: (message) => get().socket?.send(message) ?? false,

  leave: () => {
    const { socket, roomId } = get();
    socket?.send({ type: 'leave' });
    socket?.close();
    if (roomId) clearSession(roomId);
    set({ socket: null, connection: 'closed', closedReason: 'left' });
  },

  finishReveal: () => set({ reveal: null }),

  reset: () => {
    get().socket?.close();
    set({ roomId: null, selfId: null, state: null, connection: 'idle', closedReason: null, reveal: null, socket: null });
  },
}));

type Set = (partial: Partial<RoomStore>) => void;
type Get = () => RoomStore;

function nicknameOf(state: PublicRoomState | null, participantId: string): string {
  return state?.participants.find((p) => p.id === participantId)?.nickname ?? '참여자';
}

function handleMessage(message: ServerMessage, set: Set, get: Get): void {
  const current = get().state;

  if (message.type === 'room:closed') {
    set({ connection: 'closed', closedReason: message.reason });
    return;
  }
  if (message.type === 'room:error') {
    if (message.fatal) {
      const reason: ClosedReason = message.code === 'KICKED' ? 'kicked' : message.code === 'ROOM_NOT_FOUND' ? 'expired' : 'unauthorized';
      const roomId = get().roomId;
      if (roomId && (reason === 'kicked' || reason === 'unauthorized')) clearSession(roomId);
      set({ connection: 'closed', closedReason: reason });
    } else {
      toast(message.message, 'error');
    }
    return;
  }
  if (message.type === 'pong') return;

  // 상태 버전 역전 방지: 오래된 이벤트는 무시 (room:joined는 재접속 시 기준점이므로 항상 적용)
  if (message.type !== 'room:joined' && current && message.version < current.version) return;

  const previous = current;
  set({ state: message.state });

  switch (message.type) {
    case 'room:joined':
      set({ selfId: message.self.participantId });
      break;
    case 'participant:joined':
      if (message.participant.id !== get().selfId) toast(`${message.participant.nickname}님이 입장했어요 👋`);
      break;
    case 'participant:left': {
      if (message.participantId === get().selfId) break;
      const text =
        message.reason === 'kicked'
          ? `${message.nickname}님이 내보내졌어요.`
          : message.reason === 'timeout'
            ? `${message.nickname}님의 연결이 끊어져 나갔어요.`
            : `${message.nickname}님이 나갔어요.`;
      toast(text);
      break;
    }
    case 'participant:disconnected':
      if (message.participantId !== get().selfId) toast(`${nicknameOf(message.state, message.participantId)}님이 잠시 연결이 끊어졌어요.`);
      break;
    case 'participant:reconnected':
      if (message.participantId !== get().selfId) toast(`${nicknameOf(message.state, message.participantId)}님이 다시 연결됐어요.`, 'success');
      break;
    case 'game:selectionMade': {
      const reduce = reducedMotion();
      const base = message.selection.isRandom ? TIMING.RANDOM_REVEAL_MS : TIMING.SELECT_REVEAL_MS;
      set({
        reveal: {
          stage: message.selection.stage,
          optionId: message.selection.optionId,
          optionIds: message.previousOptionIds,
          isRandom: message.selection.isRandom,
          isAuto: message.selection.isAuto,
          participantNickname: message.selection.participantNickname,
          durationMs: reduce ? 350 : base,
          startedAt: Date.now(),
        },
      });
      break;
    }
    case 'game:restarted':
      set({ reveal: null });
      toast('새 게임이 시작됐어요! 순서를 새로 정했어요 🎲', 'success');
      break;
    case 'room:updated':
      if (previous && previous.hostId !== message.state.hostId) {
        const newHost = message.state.participants.find((p) => p.id === message.state.hostId);
        if (newHost) toast(newHost.id === get().selfId ? '이제 당신이 방장이에요 👑' : `${newHost.nickname}님이 새 방장이 됐어요 👑`);
      }
      break;
    default:
      break;
  }
}
